'use client'
import React, { useEffect, useState } from "react";
import type { CommentObject, NewsCardMeta } from "@/lib/types";
import { getNewsComments } from "@/actions/news";
import { ArrowUpCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { formatTimeAgo } from "@/lib/news_helpers";
import AddComments from "./AddComments";
import { ChatBubbleOvalLeftEllipsisIcon } from "@heroicons/react/24/outline";
import { postComment } from "@/actions/user_specific";
import { useAuthUserContext } from "@/contexts/authUserContext";
import { UserCircleIcon } from "@heroicons/react/24/solid";
import { useInView } from "react-intersection-observer";
import { trackClick } from "@/lib/analytics";

type CommentsProps = {
  cardId: string;
tenant: string;
commentCount: number;
justShowIcon?: boolean;
};

export default function Comments({ cardId, tenant, commentCount, justShowIcon = false }: CommentsProps) {
  const [comments, setComments] = useState<CommentObject[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {user} = useAuthUserContext();
  const {ref, inView} = useInView({
    threshold: 0.1,
    onChange: (inView) => {
      if (inView && hasMore && !loading) {
        console.log('Loading more items...');
        fetchComments(offset);
      }
    }
  })
  
  // Fetch comments
  async function fetchComments(offset: string | null = null) {
    try {
      setLoading(true);
      let response = await getNewsComments(tenant, cardId, offset, 10)
      if ('error' in response) {
        throw new Error(response.message);
      }
      if (offset) {
        setComments((prev) => [...(prev || []), ...(response.user_comments || [])]);
      } else {
        setComments(response.user_comments)
      }
      setOffset(response.min_comment_id || null);
      setHasMore(response.reload_required || false);
    } catch (error) {
      console.error("Error fetching comments:", error);
      setError("Failed to load comments. Please try again later.");
      setComments([])
    } finally {
      setLoading(false);
    }
  }

  const handleAddComment = async (comment: string) => {
    if (!comment.trim()) return;
    try {
      setLoading(true);
      let comment_id = Math.random().toString(36).substring(2, 15);
      setComments((prev) => [...(prev || []), {
        comment: comment, 
        comment_id: comment_id,
        created_at: new Date().valueOf(),
        user_name: user?.displayName || "Anonymous",
        user_photo: user?.photoURL || "",
        user_id: user?.uid || "",
      } as CommentObject ]);
      const newComment = await postComment({
        comment,
        post_id: cardId,
        // update_time: new Date().valueOf(),
      }, user?.uid);
      if ('error' in newComment) {
        throw new Error(newComment.message);
      }
      console.log('newComment:', newComment);
      
      setComments((prev) => prev?.map(el => el.comment_id === comment_id ? {...el, ...newComment } : el) || []);
      // Update comments state with the new comment
    } catch (error) {
      console.error("Failed to add comment:", error);
    } finally {
      setLoading(false);
    }
  }

  const showCommentsList = () => {
    setShowAll(true);
    trackClick(justShowIcon ? 'icon_show_all_comments' : 'show_all_comments');
  }

  const hideCommentsList = () => {
    setShowAll(false);
    trackClick('hide_comments_list');
  }

  useEffect(() => {
    fetchComments()
  }, [cardId, tenant]);

  if (justShowIcon && !showAll) {
    return <button onClick={showCommentsList} className="flex items-center space-x-1 hover:text-blue-500 transition-colors duration-200">
              <ChatBubbleOvalLeftEllipsisIcon className="h-6 w-6 text-black" />
              <span className="text-sm font-medium">{commentCount > 0 ? commentCount : ""}</span>
            </button>
  }

  if (!comments) {
    return null
  }

  if (!loading && (!comments || comments.length === 0) && !justShowIcon)
    return (
      <div className="text-gray-500 text-sm mt-2">No comments yet.</div>
    );

  return (
    <div className="mx-4 mt-2 mb-2 bg-[#F9F9F9] rounded-md py-2 px-4 box-border">
      <h2 className="flex justify-between items-center">
        <span className="font-sm font-bold">
          Comments {commentCount}
        </span>
        <button
          className="ml-2 text-primary text-xs font-semibold cursor-pointer"
          onClick={showCommentsList}
        >
          View all
        </button>
      </h2>
      {!showAll ? (
        <Comment {...(comments || [])[0]}/>
      ) : (
        <div className="fixed left-0 bottom-0 right-0 w-full lg:w-[61%] rounded-t-3xl bg-white z-50 shadow-lg border-t border-gray-200"
             style={{ height: "50vh" }}>
          <div className="flex justify-between items-center p-4 border-b border-b-light-gray">
            <span className="font-semibold">Comments</span>
            <button
              className="text-gray-500 text-sm cursor-pointer"
              onClick={hideCommentsList}
            >
              <XCircleIcon className="h-6 w-6 text-black" /> 
            </button>
          </div>
          <div className="overflow-y-auto no-scrollbar px-4 py-2 pb-6" style={{ height: "calc(50vh - 96px)" }}>
            {(comments || []).map((c) => (
              <Comment key={c.comment_id} {...c} />
            ))}
            {
<div ref={ref} className='w-full flex justify-center items-center mt-2 col-span-full'>
        {comments.length === 0 && loading && <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary"></div>}
        </div>            }
          </div>
          <AddComments
            onAddComment={handleAddComment}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
}

export const Comment = ({
  comment, user_name, user_photo, created_at, comment_id
}: CommentObject) => {
  return (
    <div className="flex items-start gap-2 my-2">
      {
        user_photo ? 
        <img
          src={user_photo}
          alt={user_name}
          className="w-7 h-7 rounded-full object-cover mt-1"
        /> : <UserCircleIcon className="w-7 h-7 rounded-full object-cover mt-1" />
      }
      <div>
        <div className="text-sm font-medium">{user_name} • <span className="font-normal font-xs">{formatTimeAgo(created_at)}</span> </div>
        <div className="text-gray-700 text-sm">{comment}</div>
      </div>
    </div>
  );
}